import React, { useEffect } from 'react';
import { Button, Col, Row } from 'reactstrap';
import UploadImage from '../assets/img/upload.svg';
import { Image } from 'react-bootstrap';
import '../assets/style/MediaUpload.css';
import Divider from '@mui/material/Divider';
import CloseIcon from '@mui/icons-material/Close';
import MTextField from './MTextField';
import classNames from 'classnames';
import { mediaUploadHandleFiles } from '../pages/reimbursement/ReimbursementHelper';

interface IMediaUploadPropTypes {
  maxFiles: number;
  supportedFiles?: string[];
  selectedFiles: File[];
  setSelectedFiles: (arg: any) => void;
  uploadFiles: (files: File[]) => void;
  maxFileSizeInBytes?: number;
  showOrderInput?: boolean;
  order?: string;
  setOrder?: (params: string) => void;
  disabled?: boolean;
}

const MediaUpload: React.FunctionComponent<IMediaUploadPropTypes> = ({
  maxFiles,
  supportedFiles = ['.mp4', '.png', '.jpeg'],
  setSelectedFiles,
  uploadFiles,
  selectedFiles,
  maxFileSizeInBytes,
  showOrderInput,
  order,
  setOrder = () => {},
  disabled,
}) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!selectedFiles?.length && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [selectedFiles]);

  const handleDrop = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    mediaUploadHandleFiles(
      Array.from(event.dataTransfer.files || []),
      supportedFiles,
      selectedFiles,
      maxFileSizeInBytes,
      maxFiles,
      setSelectedFiles
    );
  };

  const handleBrowseFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    mediaUploadHandleFiles(
      Array.from(event.target.files || []),
      supportedFiles,
      selectedFiles,
      maxFileSizeInBytes,
      maxFiles,
      setSelectedFiles
    );
  };

  const handleDragOver = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };
  const handleReset = () => {
    setSelectedFiles([]);
    setOrder('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.type = 'file';
    }
  };
  const getFileName = (className?: string) => {
    return (
      <>
        {selectedFiles?.length === 1 && (
          <span className={`selected-file ${className}`}>
            {selectedFiles[0].name}
          </span>
        )}
        {selectedFiles?.length > 1 && (
          <span className={`selected-file ${className}`}>
            {selectedFiles?.length} Files
          </span>
        )}
      </>
    );
  };

  const removeFile = (index: number) => {
    const tempFiles = [...selectedFiles];
    tempFiles.splice(index, 1);
    setSelectedFiles(tempFiles);
  };
  return (
    <div
      className={classNames('media-upload-main-container', {
        disabled: disabled,
      })}
    >
      <div className="media-upload-container">
        {showOrderInput ? (
          <Row className="ps-3 mt-2">
            <Col md={12} lg={6}>
              <MTextField
                label="Order"
                value={order ?? ''}
                onChange={(event) => {
                  const value = event.target.value;
                  if (value === '' || /^\d+$/.test(value)) {
                    setOrder(event.target.value);
                  }
                }}
                required
              />
            </Col>
          </Row>
        ) : (
          ''
        )}
        <div className="p-3">
          <span className="fw-bold">Media Upload</span>
          <br />
          <span className="subtitle">
            Add your documents here, and you can upload up to {maxFiles}
            {maxFiles === 1 ? ' file' : ' files'} max
          </span>
        </div>
        <div className="ms-3 me-3">
          <button
            className="d-flex align-items-center flex-column drag-container drag-container"
            tabIndex={0}
            onDrop={(event: any) => {
              handleDrop(event);
            }}
            onDragOver={(event: any) => {
              handleDragOver(event);
            }}
          >
            <div>
              <Image src={UploadImage} width={50} height={50} />
            </div>
            <p className="fw-bold mt-2 mb-1">
              Drag your file(s) to start uploading
            </p>
            <div className="m-1 w-50">
              <Divider>OR</Divider>
            </div>
            <div className="d-flex align-items-center">
              {getFileName('selected-file-trans')}
              <Button
                variant="outline-primary"
                className="mx-2 cl-btn-outline-primary cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                Browse Files
              </Button>
              <input
                aria-label="upload"
                type="file"
                ref={fileInputRef}
                className="display-none"
                multiple
                accept={supportedFiles.join(',')}
                onChange={handleBrowseFiles}
              />
              {getFileName('')}
            </div>
          </button>
        </div>
        <div className="p-3 subtitle mb-3">
          Only supports {supportedFiles.join(', ')} files{' '}
          {maxFileSizeInBytes &&
            `(Max file size: ${maxFileSizeInBytes / (1024 * 1024)} MB)`}
        </div>
        <div className="p-3 pt-0">
          {selectedFiles?.length > 0 && (
            <Row>
              {selectedFiles.map((file: any, index: number) => (
                <Col
                  lg={6}
                  xs={12}
                  key={`uploading-files-${String(index)}`}
                  className="pt-3"
                >
                  <div className="position-relative pe-2 uploading-files-card ">
                    <CloseIcon
                      className="uploading-file-remove-icon"
                      onClick={() => removeFile(index)}
                    />
                    <div className="file-details-container flex-grow-1">
                      <div className="d-flex justify-content-between align-items-center pe-3">
                        <p className="file-name mb-0">
                          <small>{file.name}</small>
                        </p>
                      </div>
                      <p className="file-size mb-0">
                        <small>{`Size: ${(file.size / (1024 * 1024)).toFixed(2)} MB`}</small>
                      </p>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}
        </div>
        <div className="media-upload-footer p-2">
          <Button
            variant="primary"
            className="cl-btn-primary"
            onClick={() => {
              uploadFiles(selectedFiles);
            }}
            disabled={!selectedFiles?.length}
          >
            Upload
          </Button>
          <Button
            variant="outline-primary"
            className="mx-2 cl-btn-outline-primary"
            onClick={() => {
              handleReset();
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MediaUpload;
